import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { ClBaseComponent } from '../cl-base/cl-base.component';
import { Resource } from 'src/app/components/catalogs/beamline-catalog/resource/resources';
import { FormStatusEnum, ResourceType, Roles, WFSaveOptions } from 'src/app/common/enumerations/enumerations';
import { PV } from 'src/app/services/pv-info/pv-info';
import { Checklist } from 'src/app/components/checklists/checklists';
import { YesNoDialogComponent } from '../../yes-no-dialog/yes-no-dialog.component';

export interface VacuumItem {
  id: number;
  name: string;
  channelName?: string | null;
  location?: string | null;
  textValue?: string | null;
  min?: number | null;
  max?: number | null;
  isValid?: boolean;
  autoChecked?: boolean;
  suggested?: boolean;
  manualValue?: boolean;
  checked: boolean;
  index: number;
  user?: any;
  date?: Date | null;
}

export interface VacuumGroup {
  min?: number | null;
  max?: number | null;
  vacuums: VacuumItem[];
}

@Component({
  selector: 'cl-vacuums',
  templateUrl: './cl-vacuums.component.html',
  styleUrls: ['./cl-vacuums.component.scss']
})
export class ClVacuumsComponent extends ClBaseComponent implements OnInit, OnChanges, OnDestroy {

  @Input() resourceID?: number;

  groups: VacuumGroup[] = [];

  private resourcesSubs?: Subscription;
  private autoSavedValue?: string;
  private resources: Resource[] = [];
  private pvs: PV[] = [];
  private pvSnapshotInitialized = false;
  private manuallyUncheckedVacuumIDs = new Set<number>();

  /**
   * Starts resource and PVInfo hydration for the vacuum checklist rows.
   */
  ngOnInit(): void {
    this.loadResources();
  }

  /**
   * Rebuilds vacuum rows when their resource, numbering, or persisted task value changes.
   * @param changes Updated component inputs.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resourceID'] || changes['number'] || changes['valueString']) {
      this.loadResources();
    }
  }

  /**
   * Releases the combined Resources and PVInfo store subscription.
   */
  override ngOnDestroy(): void {
    this.resourcesSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Builds ordered vacuum groups and hydrates unchecked execution rows from current PVInfo values.
   */
  loadResources() {
    this.resourcesSubs?.unsubscribe();
    let firstEmission = true;
    this.resourcesSubs = combineLatest([
      this.store.select(state => state.Resources.data),
      this.store.select(state => state.PV.data)
    ]).subscribe(([data, pvs]: [Resource[], PV[]]) => {
      if (!data.length || !this.resourceID) return;

      const resourcesChanged = this.resources !== data;
      this.resources = data;
      // Capture the first available PV list; later SignalR updates wait for the explicit refresh action.
      let snapshotInitialized = false;
      if (!this.pvSnapshotInitialized && pvs.length) {
        this.pvs = [...pvs];
        this.pvSnapshotInitialized = true;
        snapshotInitialized = true;
      }
      if (firstEmission || resourcesChanged || snapshotInitialized)
        this.buildVacuumGroups();

      firstEmission = false;
    });
  }

  /**
   * Rebuilds vacuum rows from the current resource list and cached PV snapshot.
   */
  private buildVacuumGroups(resetValues = false): void {
    if (!this.resources.length || !this.resourceID)
      return;

    const shutter = this.resources.find(r => r.id === this.resourceID);

    // Prefer childResources on the shutter; fall back to scanning all vacuums whose parentResources include the shutter
    let vacuumResources: Resource[] = [];
    const fromChildRelations = shutter?.childResources?.filter(cr => cr.childResourceType === ResourceType.Vacuum) ?? [];

    if (fromChildRelations.length) {
      vacuumResources = fromChildRelations
        .map(cr => this.resources.find(r => r.id === cr.childResourceID))
        .filter((r): r is Resource => !!r);
    } else {
      // The Resources store fills `type`; `resourceTypeID` is only present on some catalog payloads.
      vacuumResources = this.resources.filter(r =>
        (r.resourceTypeID ?? r.type) === ResourceType.Vacuum &&
        r.parentResources?.some(pr => pr.parentResourceID === this.resourceID)
      );
    }

    const saved: VacuumItem[] = this.getValueObjects(this.valueString) ?? [];
    const currentItems = this.allVacuums;
    const groupMap = new Map<string, VacuumGroup>();
    let counter = parseInt(this.number ?? '1');

    for (const resource of vacuumResources) {
      const min = resource.keVacuumMinRequirement ?? null;
      const max = resource.keVacuumMaxRequirement ?? null;
      const key = `${min ?? ''}-${max ?? ''}`;

      // Refresh intentionally discards every prior value, check, user, and date before applying current PVs.
      const savedItem = resetValues ? undefined : saved.find(s => s.id === resource.id);
      const currentItem = resetValues ? undefined : currentItems.find(item => item.id === resource.id);
      const manualValue = savedItem?.manualValue ?? currentItem?.manualValue ?? false;
      const effectiveSavedItem: VacuumItem | undefined = manualValue
        ? {
            ...(savedItem ?? currentItem as VacuumItem),
            manualValue,
            textValue: savedItem?.textValue ?? currentItem?.textValue
          }
        : savedItem;
      const textValue = this.getVacuumTextValue(resource, effectiveSavedItem, this.pvs);
      const isValid = this.isVacuumValueValid(resource, effectiveSavedItem, this.pvs);
      const manuallyChecked = !!savedItem?.checked && !savedItem.autoChecked;
      // A valid PV reading recommends a manual check; it must not check or sign the reading as ASCC.
      const suggested = this.isExecutionMode &&
        !manuallyChecked &&
        !manualValue &&
        !this.manuallyUncheckedVacuumIDs.has(resource.id ?? 0) &&
        isValid;
      const checked = manuallyChecked;
      const item: VacuumItem = {
        id: resource.id ?? 0,
        name: resource.name ?? '',
        channelName: resource.channelName ?? null,
        location: resource.location ?? null,
        textValue,
        min,
        max,
        isValid,
        autoChecked: false,
        suggested,
        manualValue,
        index: counter++,
        checked,
        user: manuallyChecked
          ? savedItem?.user ?? currentItem?.user ?? this.getCurrentUser(true)
          : null,
        date: checked ? savedItem?.date ?? currentItem?.date ?? new Date() : null,
      };

      if (!groupMap.has(key)) {
        groupMap.set(key, { min, max, vacuums: [] });
      }
      groupMap.get(key)!.vacuums.push(item);
    }

    this.groups = Array.from(groupMap.values());
    this.itemCountChanged.emit(this.allVacuums.length);
    if (resetValues) {
      this.autoSavedValue = this.utils.JSONstringify(this.allVacuums);
      // The refresh clears every check, so the result is saved without stamping a signature.
      setTimeout(() => this.saveAutomaticValues(this.allVacuums));
    } else {
      this.saveAutoCheckedValues(saved);
    }
  }

  /**
   * Indicates whether the refresh control should be shown for the current execution section.
   */
  get showRefreshVacuumValues(): boolean {
    return this.isExecutionMode;
  }

  /**
   * Allows FO and VAC users to refresh values while the Generic KE checklist is Active.
   */
  get canRefreshVacuumValues(): boolean {
    const checklist = this.checklist as Checklist | null | undefined;
    // Checklist.status is the record status; execution workflow state comes from ChecklistExecStatus.
    const status = checklist?.checklistStatus?.formStatusID ?? checklist?.status;
    return this.showRefreshVacuumValues &&
      status === FormStatusEnum.Active &&
      !!this.hasRoles([Roles.FO, Roles.VAC]);
  }

  /**
   * Confirms the destructive refresh, clears all completion metadata, and applies the latest PV snapshot.
   */
  async refreshVacuumValues(): Promise<void> {
    if (!this.canRefreshVacuumValues)
      return;

    const dialogRef = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('KEResetVacuumValues').description,
        icon: 'stop'
      }
    });
    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed)
      return;

    this.pvs = [...this.store.selectSnapshot(state => state.PV.data)];
    this.pvSnapshotInitialized = true;
    this.autoSavedValue = undefined;
    // Refresh clears manual unchecks and all persisted completion metadata before revalidation.
    this.manuallyUncheckedVacuumIDs.clear();
    this.buildVacuumGroups(true);
  }

  /**
   * Persists changed PV recommendations, and clears legacy ASCC auto-checks, without signing the task.
   * @param saved Previously persisted vacuum values.
   */
  private saveAutoCheckedValues(saved: VacuumItem[]): void {
    if (!this.isExecutionMode)
      return;

    const hasAutomaticChanges = this.allVacuums.some(vacuum => {
      const savedVacuum = saved.find(item => item.id === vacuum.id);
      // Legacy rows were checked by ASCC; they are saved again as unchecked recommendations.
      return !!savedVacuum?.autoChecked ||
        !!savedVacuum?.suggested !== !!vacuum.suggested ||
        (!!vacuum.suggested && savedVacuum?.textValue !== vacuum.textValue);
    });
    if (!hasAutomaticChanges)
      return;

    const serializedValue = this.utils.JSONstringify(this.allVacuums);
    if (serializedValue === this.autoSavedValue)
      return;

    this.autoSavedValue = serializedValue;
    // Defer the output until the current input/store update has completed.
    setTimeout(() => this.saveAutomaticValues(this.allVacuums));
  }

  /**
   * Saves PV-driven vacuum values as an AUTO update so the backend never stamps a signature for them.
   * @param values Vacuum rows to persist.
   */
  private saveAutomaticValues(values: VacuumItem[]): void {
    this.save.emit({ text: this.utils.JSONstringify(values), option: WFSaveOptions.AUTO });
  }

  /**
   * Explains why a valid, unchecked reading is recommended for a manual check.
   * @param vacuum Vacuum row with a valid live reading.
   */
  vacuumSuggestionReason(vacuum: VacuumItem): string {
    return `${vacuum.name} is within ${this.stripTags(this.vacuumRequirement(vacuum))} Torr. Review the reading and check it manually.`;
  }

  /**
   * Removes HTML markup from scientific-notation labels used in tooltips.
   * @param value Text that may contain sup tags.
   */
  private stripTags(value: string): string {
    return value.replace(/<sup>/g, '^').replace(/<[^>]+>/g, '');
  }

  /**
   * Returns true outside builder/editor contexts where live PVInfo values should be used.
   */
  get isExecutionMode(): boolean {
    return !this.editor && !this.builder;
  }

  /**
   * Resolves the displayed vacuum value, preserving checked audit values and otherwise using PVInfo.
   * @param resource Vacuum resource containing the configured channel.
   * @param savedItem Persisted checklist value, when available.
   * @param pvs Current PVInfo list.
   */
  private getVacuumTextValue(resource: Resource, savedItem: VacuumItem | undefined, pvs: PV[]): string | null {
    if (!this.isExecutionMode || savedItem?.manualValue || (savedItem?.checked && !savedItem.autoChecked))
      return savedItem?.textValue ?? null;

    const value = this.getPVValue(resource.channelName, pvs);
    return value == null ? '<b class="red">Error!</b>' : this.formatSciNotation(value);
  }

  /**
   * Validates the current execution value against the configured inclusive minimum and maximum.
   * @param resource Vacuum resource containing limits and channel.
   * @param savedItem Persisted checklist value, when available.
   * @param pvs Current PVInfo list.
   */
  private isVacuumValueValid(resource: Resource, savedItem: VacuumItem | undefined, pvs: PV[]): boolean {
    if (!this.isExecutionMode)
      return true;

    const value = savedItem?.manualValue || (savedItem?.checked && !savedItem.autoChecked)
      ? this.parseVacuumValue(savedItem.textValue)
      : this.getPVValue(resource.channelName, pvs);

    return value != null &&
      value !== 0 &&
      value >= (resource.keVacuumMinRequirement ?? 0) &&
      value <= (resource.keVacuumMaxRequirement ?? 0);
  }

  /**
   * Reads and normalizes a channel's numeric value from the PVInfo list.
   * @param channelName Configured EPICS channel name.
   * @param pvs Current PVInfo list.
   */
  private getPVValue(channelName: string | null | undefined, pvs: PV[]): number | null {
    const pv = pvs.find(item => item.pvName?.toUpperCase() === channelName?.toUpperCase());
    return this.parseVacuumValue(pv?.value);
  }

  /**
   * Converts plain, exponential, or displayed scientific notation into a numeric value.
   * @param value Vacuum value to normalize.
   */
  private parseVacuumValue(value?: string | number | null): number | null {
    if (value == null)
      return null;

    const parsed = Number(value.toString()
      .replace(/<[^>]+>/g, '')
      .replace(/\s/g, '')
      .toLowerCase()
      .replace('x10^', 'e')
      .replace('x10', 'e'));

    return Number.isFinite(parsed) ? parsed : null;
  }

  /**
   * Formats a numeric vacuum value using the scientific notation used by old KE execution.
   * @param value Vacuum value to display.
   */
  private formatSciNotation(value: number): string {
    if (value === 0)
      return '0.00';

    return value.toExponential(2)
      .replace('e', ' x 10')
      .replace('-', '<sup>-')
      .replace('+', '<sup>');
  }

  /**
   * Combines configured task permissions with live PV validity.
   * VAC users retain edit access to correct an invalid reading.
   * @param vacuum Vacuum row being evaluated.
   */
  vacuumDisabled(vacuum: VacuumItem): boolean {
    return !!this.disabled ||
      (this.isExecutionMode && !vacuum.isValid && !this.hasRoles([Roles.VAC]));
  }

  /**
   * Builds the vacuum range hint using the same scientific notation as the grouping header.
   * @param vacuum Vacuum row containing configured limits.
   */
  vacuumRequirement(vacuum: VacuumItem): string {
    return `${this.toSciNotation(vacuum.min ?? 0)} to ${this.toSciNotation(vacuum.max ?? 0)}`;
  }

  get allVacuums(): VacuumItem[] {
    return this.groups.flatMap(g => g.vacuums);
  }

  groupLabel(group: VacuumGroup): string {
    const parts: string[] = [];
    if (group.min != null && group.min !== 0) parts.push(`&gt; ${this.toSciNotation(group.min)}`);
    if (group.max != null && group.max !== 0) parts.push(`&lt; ${this.toSciNotation(group.max)}`);
    return `The following pressures should be ${parts.join(' and ')} Torr.`;
  }

  toSciNotation(value: number): string {
    if (value === 0) return '0';
    const exp = Math.floor(Math.log10(Math.abs(value)));
    const mantissa = Math.round((value / Math.pow(10, exp)) * 1000) / 1000;
    const mantissaStr = Number.isInteger(mantissa) ? `${mantissa}` : `${mantissa}`;
    return exp === 0 ? mantissaStr : `${mantissaStr} x 10<sup>${exp}</sup>`;
  }

  vacuumTextBefore(vacuum: VacuumItem): string {
    return `<b>${vacuum.name}</b> =`;
  }

  vacuumTextAfter(vacuum: VacuumItem): string {
    return vacuum.location ? `Torr. <b>on ${vacuum.location}</b>` : 'Torr.';
  }

  vacuumPlaceholder(vacuum: VacuumItem): string {
    return vacuum.channelName ? `EPICS - [${vacuum.channelName}]` : 'EPICS';
  }

  /**
   * Updates completion metadata using the boolean emitted by the checkbox control.
   * @param vacuum Vacuum row whose checkbox changed.
   * @param checked New checkbox state.
   */
  checkboxChanged(vacuum: VacuumItem, checked: boolean) {
    vacuum.checked = checked;
    vacuum.autoChecked = false;
    vacuum.suggested = false;
    if (checked)
      this.manuallyUncheckedVacuumIDs.delete(vacuum.id);
    else
      this.manuallyUncheckedVacuumIDs.add(vacuum.id);
    vacuum.user = checked ? this.getCurrentUser(true) : null;
    vacuum.date = checked ? new Date() : null;
    this.saveValues(this.allVacuums);
  }

  /**
   * Revalidates a manually corrected value and synchronizes its completion metadata.
   * @param vacuum Vacuum row whose value changed.
   * @param value Updated text value.
   */
  textChanged(vacuum: VacuumItem, value: string | null) {
    vacuum.textValue = value;
    vacuum.manualValue = true;
    const numericValue = this.parseVacuumValue(value);
    vacuum.isValid = numericValue != null &&
      numericValue !== 0 &&
      numericValue >= (vacuum.min ?? 0) &&
      numericValue <= (vacuum.max ?? 0);
    // The text-input control completes valid manual entries; persist the same state in the task value.
    vacuum.checked = vacuum.isValid;
    vacuum.autoChecked = false;
    vacuum.suggested = false;
    vacuum.user = vacuum.isValid ? this.getCurrentUser(true) : null;
    vacuum.date = vacuum.isValid ? new Date() : null;
    this.saveValues(this.allVacuums);
  }
}
